import React, { useEffect, useMemo, useRef, useState } from 'react';
import { safeArray } from '../../../components/ErrorBoundary';
import TableEmptyRow from '../../../components/TableEmptyRow';
import StatusBadge from '../../../components/StatusBadge';
import { banksAPI, bankLimitsAPI } from '../../../services/bgFinanceService';
import { emptyLimitForm, formatCurrency, formatPercent, LIMIT_TYPE_LABELS, LIMIT_TYPE_OPTIONS } from './bgShared';
import BgExportButtons from './BgExportButtons';
import { downloadBgTableCsv, exportBgTablePdf } from './bgExportUtils';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const mapLimit = (row) => ({
  id: row.id,
  bank_id: row.bank_id,
  bank_name: row.bank?.bank_name || row.bank_name || '—',
  limit_name: row.limit_name || '',
  limit_type: row.limit_type || 'BG_LIMIT',
  sanctioned_amount: row.sanctioned_amount ?? 0,
  eligible_amount: row.eligible_amount,
  margin_percent: row.margin_percent,
  effective_from: row.effective_from || '',
  effective_to: row.effective_to || '',
  remarks: row.remarks || '',
  is_active: row.is_active !== false,
  supports_full_cash_backed: row.supports_full_cash_backed !== false,
  full_cash_backed_counts_against_limit_default: !!row.full_cash_backed_counts_against_limit_default,
  allow_full_cash_backed_limit_override: row.allow_full_cash_backed_limit_override !== false,
  full_cash_backed_policy_status: row.full_cash_backed_policy_status || 'ACTIVE',
  full_cash_backed_policy_effective_from: row.full_cash_backed_policy_effective_from || '',
  full_cash_backed_policy_remarks: row.full_cash_backed_policy_remarks || '',
  utilization: row.utilization || {},
});

const BankLimitMaster = () => {
  const [limits, setLimits] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLimitForm);
  const tableRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    try {
      const [limitsResp, banksResp] = await Promise.all([
        bankLimitsAPI.getAll(),
        banksAPI.getAll(),
      ]);
      setLimits(safeArray(limitsResp?.data?.data).map(mapLimit));
      setBanks(safeArray(banksResp?.data?.data));
    } catch (err) {
      console.error('Load bank limits failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to load bank limits.');
      setLimits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const kpis = useMemo(() => {
    const active = limits.filter((l) => l.is_active);
    return {
      eligible: active.reduce((s, l) => s + (l.utilization?.eligibleLimit ?? l.eligible_amount ?? l.sanctioned_amount ?? 0), 0),
      utilized: active.reduce((s, l) => s + (l.utilization?.utilizedAgainstLimit ?? 0), 0),
      available: active.reduce((s, l) => s + (l.utilization?.availableLimit ?? 0), 0),
      cashBlocked: active.reduce((s, l) => s + (l.utilization?.cashBlocked ?? 0), 0),
      fullCashBacked: active.reduce((s, l) => s + (l.utilization?.fullCashBackedAmount ?? 0), 0),
    };
  }, [limits]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyLimitForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      bank_id: String(row.bank_id || ''),
      limit_name: row.limit_name || '',
      limit_type: row.limit_type || 'BG_LIMIT',
      sanctioned_amount: row.sanctioned_amount != null ? String(row.sanctioned_amount) : '',
      eligible_amount: row.eligible_amount != null ? String(row.eligible_amount) : '',
      margin_percent: row.margin_percent != null ? String(row.margin_percent) : '',
      effective_from: row.effective_from || '',
      effective_to: row.effective_to || '',
      remarks: row.remarks || '',
      is_active: row.is_active !== false,
      supports_full_cash_backed: row.supports_full_cash_backed !== false,
      full_cash_backed_counts_against_limit_default: !!row.full_cash_backed_counts_against_limit_default,
      allow_full_cash_backed_limit_override: row.allow_full_cash_backed_limit_override !== false,
      full_cash_backed_policy_status: row.full_cash_backed_policy_status || 'ACTIVE',
      full_cash_backed_policy_effective_from: row.full_cash_backed_policy_effective_from || '',
      full_cash_backed_policy_remarks: row.full_cash_backed_policy_remarks || '',
    });
    setShowModal(true);
  };

  const buildPayload = () => ({
    bank_id: Number(form.bank_id),
    limit_name: form.limit_name.trim(),
    limit_type: form.limit_type,
    sanctioned_amount: Number(form.sanctioned_amount || 0),
    eligible_amount: form.eligible_amount !== '' ? Number(form.eligible_amount) : null,
    margin_percent: form.margin_percent !== '' ? Number(form.margin_percent) : null,
    effective_from: form.effective_from || null,
    effective_to: form.effective_to || null,
    remarks: form.remarks || null,
    is_active: !!form.is_active,
    supports_full_cash_backed: !!form.supports_full_cash_backed,
    full_cash_backed_counts_against_limit_default: !!form.full_cash_backed_counts_against_limit_default,
    allow_full_cash_backed_limit_override: !!form.allow_full_cash_backed_limit_override,
    full_cash_backed_policy_status: form.full_cash_backed_policy_status,
    full_cash_backed_policy_effective_from: form.full_cash_backed_policy_effective_from || null,
    full_cash_backed_policy_remarks: form.full_cash_backed_policy_remarks || null,
  });

  const handleSave = async () => {
    if (!form.bank_id || !form.limit_name.trim() || !form.sanctioned_amount) {
      notifyWarning('Bank, limit name and sanctioned amount are required.');
      return;
    }
    if (!(await confirmDialog(editing ? 'Save limit changes?' : 'Create this bank limit?', editing ? 'Update Limit' : 'New Limit'))) return;

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        const resp = await bankLimitsAPI.update(editing.id, payload);
        const mapped = mapLimit(resp?.data);
        setLimits((prev) => prev.map((l) => (l.id === editing.id ? mapped : l)));
        notifySuccess('Bank limit updated.');
      } else {
        const resp = await bankLimitsAPI.create(payload);
        const mapped = mapLimit(resp?.data);
        setLimits((prev) => [...prev, mapped]);
        notifySuccess('Bank limit created.');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Save bank limit failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to save bank limit.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!(await confirmDialog(`Deactivate limit ${row.limit_name}?`, 'Deactivate Limit'))) return;
    try {
      await bankLimitsAPI.delete(row.id);
      setLimits((prev) => prev.map((l) => (l.id === row.id ? { ...l, is_active: false } : l)));
      notifySuccess('Bank limit deactivated.');
    } catch (err) {
      console.error('Delete bank limit failed:', err);
      notifyError(err?.response?.data?.message || 'Unable to deactivate bank limit.');
    }
  };

  if (loading) {
    return <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading bank limits...</p>;
  }


  const handleExportPdf = () => exportBgTablePdf({ title: 'Bank Limit Master', subtitle: '', element: tableRef.current });
  const handleExportCsv = () => downloadBgTableCsv({ element: tableRef.current, filename: 'bank-limit-master.csv' });

  return (
    <div>
      <div className="section-header">
        <h2>Bank Limit Master <span className="count-badge">{limits.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}><BgExportButtons onDownloadPdf={handleExportPdf} onDownloadCsv={handleExportCsv} /><button type="button" className="btn-primary" onClick={openCreate}>Add Limit</button></div>
      </div>

      <div className="cards-grid kpi-cards" style={{ marginBottom: 16 }}>
        <div className="card">
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Eligible Limit</p>
          <p className="kpi-value">{formatCurrency(kpis.eligible)}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Utilized Limit</p>
          <p className="kpi-value">{formatCurrency(kpis.utilized)}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Available Limit</p>
          <p className="kpi-value">{formatCurrency(kpis.available)}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Cash Margin Blocked</p>
          <p className="kpi-value">{formatCurrency(kpis.cashBlocked)}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Full Cash-backed BG/Bond</p>
          <p className="kpi-value">{formatCurrency(kpis.fullCashBacked)}</p>
        </div>
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Bank</th>
              <th>Limit Type</th>
              <th className="amount-cell">Sanctioned Amount</th>
              <th className="amount-cell">Eligible Amount</th>
              <th className="amount-cell">Utilized Amount</th>
              <th className="amount-cell">Available Amount</th>
              <th>Utilization %</th>
              <th className="amount-cell">Cash Margin Blocked</th>
              <th>Full Cash-backed Policy</th>
              <th>Active BG Count</th>
              <th>Status</th>
              <th className="bg-export-exclude" style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {limits.length === 0 ? (
              <TableEmptyRow colSpan={12} message="No bank limits found." />
            ) : (
              limits.map((row) => {
                const util = row.utilization || {};
                const utilPct = util.utilizationPercent ?? 0;
                return (
                  <tr key={row.id}>
                    <td style={{ fontWeight: 500 }}>{row.bank_name}</td>
                    <td>{LIMIT_TYPE_LABELS[row.limit_type] || row.limit_type}</td>
                    <td className="amount-cell">{formatCurrency(row.sanctioned_amount)}</td>
                    <td className="amount-cell">{formatCurrency(util.eligibleLimit ?? row.eligible_amount ?? row.sanctioned_amount)}</td>
                    <td className="amount-cell">{formatCurrency(util.utilizedAgainstLimit ?? 0)}</td>
                    <td className="amount-cell" style={{ color: (util.availableLimit ?? 0) < 0 ? '#b91c1c' : undefined }}>{formatCurrency(util.availableLimit ?? 0)}</td>
                    <td>{formatPercent(utilPct)}</td>
                    <td className="amount-cell">{formatCurrency(util.cashBlocked ?? 0)}</td>
                    <td>{row.supports_full_cash_backed ? 'Yes' : 'No'}</td>
                    <td>{util.activeBgCount ?? 0}</td>
                    <td><StatusBadge tone={row.is_active ? 'active' : 'closed'}>{row.is_active ? 'Active' : 'Inactive'}</StatusBadge></td>
                    <td className="bg-export-exclude" style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => openEdit(row)}>Edit</button>
                      {' '}
                      {row.is_active && (
                        <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDelete(row)}>Deactivate</button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Bank Limit' : 'Add Bank Limit'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Bank <span className="req">*</span></label>
                  <select value={form.bank_id} onChange={(e) => setForm((p) => ({ ...p, bank_id: e.target.value }))}>
                    <option value="">Select Bank</option>
                    {banks.filter((b) => b.is_active !== false).map((b) => (
                      <option key={b.id} value={b.id}>{b.bank_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Limit Name <span className="req">*</span></label>
                  <input type="text" value={form.limit_name} onChange={(e) => setForm((p) => ({ ...p, limit_name: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Limit Type</label>
                  <select value={form.limit_type} onChange={(e) => setForm((p) => ({ ...p, limit_type: e.target.value }))}>
                    {LIMIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sanctioned Amount <span className="req">*</span></label>
                  <input type="number" value={form.sanctioned_amount} onChange={(e) => setForm((p) => ({ ...p, sanctioned_amount: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Eligible Amount</label>
                  <input type="number" value={form.eligible_amount} onChange={(e) => setForm((p) => ({ ...p, eligible_amount: e.target.value }))} placeholder="Defaults to sanctioned" />
                </div>
                <div className="form-group">
                  <label>Margin %</label>
                  <input type="number" value={form.margin_percent} onChange={(e) => setForm((p) => ({ ...p, margin_percent: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Effective From</label>
                  <input type="date" value={form.effective_from} onChange={(e) => setForm((p) => ({ ...p, effective_from: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Effective To</label>
                  <input type="date" value={form.effective_to} onChange={(e) => setForm((p) => ({ ...p, effective_to: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea rows={2} value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>

              <h3 style={{ fontSize: 15, fontWeight: 600, margin: '20px 0 12px', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                100% Margin Money / Cash-backed BG Policy
              </h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Policy changes apply to new BG/Bond registrations only. Existing records retain their original limit consumption settings.
              </p>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.supports_full_cash_backed} onChange={(e) => setForm((p) => ({ ...p, supports_full_cash_backed: e.target.checked }))} />
                  Supports 100% cash-backed BG/Bond
                </label>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.full_cash_backed_counts_against_limit_default} onChange={(e) => setForm((p) => ({ ...p, full_cash_backed_counts_against_limit_default: e.target.checked }))} />
                  Should 100% cash-backed BG/Bond consume this bank eligible limit by default?
                </label>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.allow_full_cash_backed_limit_override} onChange={(e) => setForm((p) => ({ ...p, allow_full_cash_backed_limit_override: e.target.checked }))} />
                  Allow per-BG override of limit consumption
                </label>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Policy Status</label>
                  <select value={form.full_cash_backed_policy_status} onChange={(e) => setForm((p) => ({ ...p, full_cash_backed_policy_status: e.target.value }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Policy Effective From</label>
                  <input type="date" value={form.full_cash_backed_policy_effective_from} onChange={(e) => setForm((p) => ({ ...p, full_cash_backed_policy_effective_from: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Policy Remarks</label>
                <textarea rows={2} value={form.full_cash_backed_policy_remarks} onChange={(e) => setForm((p) => ({ ...p, full_cash_backed_policy_remarks: e.target.value }))} style={{ width: '100%' }} />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn-primary" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankLimitMaster;
